import { useMemo, useState } from 'react';

import initializeMaps from './initializeMaps';
import {
  FilterOption,
  FilterCategory,
  DefaultFilterOption,
} from '../../models/Filter';
import isDefaultOption from './isDefaultOption';
import { buildQueryString } from './queryReducer';

export interface UseQueryBuilderParams {
  filterCategories: FilterCategory[];
}

export interface UseQueryBuilderResult {
  queryString: string;
  addFilter: (
    newOption: FilterOption,
    categoryId: string,
    options?: { queryKey: string },
  ) => void;
  removeFilter: (optionId: string, categoryId: string) => boolean;
  tagFilterList: FilterOption[];
  singularCategories: Map<string, FilterOption>;
  multipleCategories: Map<string, Map<string, FilterOption>>;
  resetFilters: () => void;
}

const useQueryBuilder = ({
  filterCategories,
}: UseQueryBuilderParams): UseQueryBuilderResult => {
  const { defaultOptionsMap, singularCategoriesMap, multipleCategoriesMap } =
    useMemo(() => initializeMaps(filterCategories), [filterCategories]);

  const [tagFilterList, setTagFilterList] = useState<FilterOption[]>([]);

  const queryString = useMemo(
    () => buildQueryString(singularCategoriesMap, multipleCategoriesMap),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tagFilterList, singularCategoriesMap, multipleCategoriesMap],
  );

  // reference to the set of categories of type singular
  const singularCategoriesSet = filterCategories.reduce<Set<string>>(
    (set, cur) => {
      if (cur.type === 'singular') {
        set.add(cur.id);
      }
      return set;
    },
    new Set<string>(),
  );

  function updateTagFilterList(): void {
    const output: FilterOption[] = [];

    const checkDefault = (filterOption: FilterOption) => {
      if (!isDefaultOption(defaultOptionsMap, filterOption)) {
        output.push(filterOption);
      }
    };

    singularCategoriesMap.forEach((filterOption) => {
      checkDefault(filterOption);
    });

    multipleCategoriesMap.forEach((category) => {
      category.forEach((filterOption) => {
        checkDefault(filterOption);
      });
    });
    setTagFilterList(output);
  }

  function addFilter(
    filter: FilterOption,
    categoryId: string,
    options?: { queryKey: string }, // user can define query key to use
  ): void {
    let usedFilter = { ...filter };
    if (typeof options !== 'undefined' && usedFilter.categoryId !== 'date') {
      usedFilter = {
        ...usedFilter,
        queryKey: options.queryKey,
      };
    }

    // singular category case
    if (singularCategoriesSet.has(categoryId)) {
      singularCategoriesMap.set(categoryId, usedFilter);
    } else {
      if (!multipleCategoriesMap.has(categoryId)) {
        multipleCategoriesMap.set(categoryId, new Map());
      }
      multipleCategoriesMap
        .get(categoryId)
        ?.set(usedFilter.id, usedFilter as DefaultFilterOption);
    }
    updateTagFilterList();
  }

  function removeFilter(optionId: string, categoryId: string): boolean {
    if (singularCategoriesSet.has(categoryId)) {
      const defaultFilter = defaultOptionsMap.get(categoryId);
      if (defaultFilter !== undefined) {
        singularCategoriesMap.set(categoryId, defaultFilter);
        updateTagFilterList();
        return true;
      }
      return false;
    }

    if (multipleCategoriesMap.has(categoryId)) {
      multipleCategoriesMap.get(categoryId)?.delete(optionId);
      updateTagFilterList();
      return true;
    }
    return false;
  }

  function resetFilters() {
    singularCategoriesMap.forEach((_singularFilter, key) => {
      singularCategoriesMap.delete(key);
    });
    multipleCategoriesMap.forEach((defaultFilterCategory, categoryKey) => {
      const defaultOptions = defaultOptionsMap.get(categoryKey)?.id;
      const currentOption = defaultFilterCategory.get(defaultOptions as string);
      // Do not clear default options
      if (!currentOption) {
        multipleCategoriesMap.get(categoryKey)?.clear();
      }
    });

    updateTagFilterList();
  }

  return {
    queryString,
    addFilter,
    removeFilter,
    resetFilters,
    tagFilterList,
    singularCategories: singularCategoriesMap,
    multipleCategories: multipleCategoriesMap,
  };
};

export default useQueryBuilder;
